import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import useLocalStorage from "../internal-libs/uselocalstorage";
import { resolveAuthoritativeAccessLevel } from "../lib/access-level-resolution";
import { ACCESS_LEVELS } from "../lib/access-levels";
import Client from "../services/client";

const UserContextAplication = createContext(undefined);

export { ACCESS_LEVELS };

const UserAplicationProvider = ({ isAuth, children }) => {
  const client = useMemo(() => new Client(), []);
  const [userAccessLevel, setUserAccessLevel] = useLocalStorage("userAccessLevel", 0);
  const [accessLevelResolved, setAccessLevelResolved] = useState(false);
  const [accessLevelResolutionId, setAccessLevelResolutionId] = useState(0);
  const cachedAccessLevelRef = useRef(userAccessLevel);
  const resolutionIdRef = useRef(0);

  const handleAccessLevel = useCallback(async () => {
    const resolutionId = ++resolutionIdRef.current;
    setAccessLevelResolved(false);
    const resolvedAccessLevel = await resolveAuthoritativeAccessLevel({
      cachedAccessLevel: cachedAccessLevelRef.current,
      loadAccessLevel: () => client.getUserLevelAccess(),
    });

    if (resolutionId === resolutionIdRef.current) {
      setUserAccessLevel(resolvedAccessLevel);
      setAccessLevelResolutionId(resolutionId);
      setAccessLevelResolved(true);
    }

    return resolvedAccessLevel;
  }, [client, setUserAccessLevel]);

  useEffect(() => {
    if (!isAuth) {
      resolutionIdRef.current += 1;
      setAccessLevelResolved(false);
      return;
    }

    setAccessLevelResolved(false);
    void handleAccessLevel();
  }, [handleAccessLevel, isAuth]);

  const context = {
    userAccessLevel,
    handleAccessLevel,
    loadedAccessLevel: accessLevelResolved,
    accessLevelResolutionId,
  };

  return (
    <>
      <UserContextAplication.Provider value={context}>{children}</UserContextAplication.Provider>
    </>
  );
};

export { UserAplicationProvider, UserContextAplication };

export function useUser() {
  return useContext(UserContextAplication) as {
    userAccessLevel: number;
    handleAccessLevel: () => Promise<number>;
    loadedAccessLevel: boolean;
    accessLevelResolutionId: number;
  };
}
